import EmailEditorComposer from '@/components/affiliate/bulk-email/EmailEditorComposer';
import EmailComposerTemplateControls from '@/components/affiliate/bulk-email/EmailComposerTemplateControls';
import SaveAsTemplatePanel from '@/components/templates/SaveAsTemplatePanel';
import { EmailConnectPrompt } from '@/components/integrations/EmailConnectPrompt';
import { MAX_BODY_LENGTH } from '@/components/affiliate/bulk-email/editor-config';
import { Alert } from '@/components/ui/alert';
import type { useEmailComposer } from '@/hooks/use-email-composer';
import { Box, Button, HStack, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import EmailComposerCanvas from './EmailComposerCanvas';
import EmailComposerSubjectRow from './EmailComposerSubjectRow';
import { EMAIL_COMPOSER_ROW_LABEL_WIDTH } from './email-composer-layout';

interface SingleRecipientEmailPanelProps {
  composer: ReturnType<typeof useEmailComposer>;
  title: string;
  recipientLabel: string;
  recipientName: string;
  recipientEmail: string;
  confirmTitle: string;
  confirmDescription: string;
  confirmBackLabel: string;
  confirmSendLabel: string;
  sendLabel: string;
  warning?: ReactNode;
  /**
   * Optional discreet note shown under the recipient address (e.g. the email's
   * provenance when it was not typed by the brand). Omitted for recipients whose
   * address the brand entered itself.
   */
  recipientCaption?: ReactNode;
  /**
   * `drawer`: 620px slide-in overlay (affiliate drawer).
   * `modal`: centered, height-capped box.
   * `inline`: fills its parent (used inside the unified contact drawer's right
   * panel, which already provides the surface/scroll — no absolute positioning).
   */
  variant?: 'drawer' | 'modal' | 'inline';
}

const SingleRecipientEmailPanel: FC<SingleRecipientEmailPanelProps> = ({
  composer,
  title,
  recipientLabel,
  recipientName,
  recipientEmail,
  confirmTitle,
  confirmDescription,
  confirmBackLabel,
  confirmSendLabel,
  sendLabel,
  warning,
  recipientCaption,
  variant = 'drawer',
}) => {
  const { t } = useTranslation();
  // `inline` renders inside the contact drawer, whose own context bar supplies the
  // title + back + close, so the panel omits its own header there.
  const showHeader = variant === 'drawer' || (variant === 'modal' && title.length > 0);
  const showCloseButton = variant === 'drawer';
  const warningSlot = warning ? (
    <Alert status="warning" rounded="md" title={warning} hideIndicator />
  ) : undefined;

  // Label and value share one flex row so the caption, stacked under the
  // recipient, keeps the name's exact left edge (a plain padding-left would
  // miss the row gap).
  const recipientSlot = (
    <HStack minW={0} gap={3} align="flex-start">
      <Text
        fontSize="sm"
        color="fg.muted"
        w={EMAIL_COMPOSER_ROW_LABEL_WIDTH}
        flexShrink={0}
        lineHeight="28px"
      >
        {recipientLabel}
      </Text>
      <Box minW={0}>
        <HStack minW={0} gap={3}>
          <Text fontSize="sm" fontWeight="medium" truncate lineHeight="28px">
            {recipientName}
          </Text>
          <Text fontSize="sm" color="fg.muted" truncate lineHeight="28px">
            {recipientEmail}
          </Text>
        </HStack>
        {recipientCaption ?? null}
      </Box>
    </HStack>
  );

  const senderSlot = composer.senderEmail ? (
    <HStack minW={0} gap={3} minH="28px">
      <Text fontSize="sm" color="fg.muted" w={EMAIL_COMPOSER_ROW_LABEL_WIDTH} flexShrink={0}>
        {t('bulkEmail.compose.from')}:
      </Text>
      <Text fontSize="sm" fontWeight="medium" truncate>
        {composer.senderEmail}
      </Text>
    </HStack>
  ) : undefined;

  const subjectSlot = (
    <EmailComposerSubjectRow
      resetKey={composer.resetKey}
      availableVariables={composer.availableVariables}
      onContentChange={composer.setSubject}
    />
  );

  const counterSlot =
    composer.bodyLength > 0 ? (
      <Text fontSize="xs" color={composer.counterColor} textAlign="right">
        {composer.bodyLength.toLocaleString()} / {MAX_BODY_LENGTH.toLocaleString()}
      </Text>
    ) : undefined;

  const bodySlot = (
    <EmailEditorComposer
      resetKey={composer.bodySeedKey}
      availableVariables={composer.availableVariables}
      attachments={composer.attachments}
      isPreviewing={composer.isPreviewing}
      previewHtml={composer.previewCompiled.body}
      onBodyChange={composer.setBody}
      onTogglePreview={composer.handleTogglePreview}
      onFileSelect={composer.handleFileSelect}
      onRemoveAttachment={composer.handleRemoveAttachment}
      deletingId={composer.deletingId}
      isUploading={composer.isUploading}
      attachmentStatus={
        composer.attachmentControls
          ? {
              issues: composer.attachmentControls.issues,
              onDismissIssue: composer.attachmentControls.dismissIssue,
              validationError: composer.attachmentControls.validationError,
              maxTotalBytes: composer.attachmentControls.maxTotalBytes,
            }
          : undefined
      }
      initialContent={composer.initialContent}
      onSnapshotChange={composer.onSnapshotChange}
      variant="document"
      footerCounterSlot={counterSlot}
      footerTemplateSlot={
        <EmailComposerTemplateControls
          templateFlow={composer.templateFlow}
          disabled={composer.isPreviewing}
        />
      }
    />
  );

  const content = (
    <>
      {showHeader && (
        <HStack px={5} py={4} justify="space-between" flexShrink={0}>
          <Text fontWeight="semibold" fontSize="md">
            {title}
          </Text>
          {showCloseButton && (
            <Button variant="ghost" colorPalette="gray" size="xs" onClick={composer.handleClose}>
              {t('common.close')}
            </Button>
          )}
        </HStack>
      )}

      {!composer.isConnected ? (
        <Box px={5} pt={showHeader ? 0 : 4} flex={1}>
          <EmailConnectPrompt />
        </Box>
      ) : (
        <>
          <Box
            flex={1}
            minH={0}
            display="flex"
            flexDirection="column"
            {...(variant === 'inline'
              ? { px: 0, pt: 0, pb: 0 }
              : { px: 5, pt: showHeader ? 0 : 4, pb: 4 })}
          >
            <EmailComposerCanvas
              step={composer.step}
              editLabel={
                composer.step === 'confirm' ? confirmBackLabel : t('bulkEmail.compose.edit')
              }
              previewLabel={t('bulkEmail.compose.preview')}
              sendLabel={sendLabel}
              confirmTitle={confirmTitle}
              confirmDescription={confirmDescription}
              confirmSendLabel={confirmSendLabel}
              canProceed={composer.canProceed}
              isSending={composer.isSending}
              isPreviewing={composer.isPreviewing}
              onEdit={() => composer.setStep('compose')}
              onSendClick={() => composer.setStep('confirm')}
              onConfirmSend={composer.handleSend}
              onTogglePreview={composer.handleTogglePreview}
              warning={warningSlot}
              senderSlot={senderSlot}
              recipientSlot={recipientSlot}
              subjectSlot={subjectSlot}
              bodySlot={bodySlot}
              framed={variant !== 'inline'}
            />
          </Box>

          {/* `inline` (contact drawer) and `modal` (outreach dialog) each supply
              their own external header/bar above the composer. The overlay
              covers that too and becomes the single header, so it shows a back
              affordance. `drawer` has no external bar — its own header lives
              inside the composer the overlay already covers — so no chevron. */}
          <SaveAsTemplatePanel
            open={composer.templateFlow.isSaveDrawerOpen}
            seed={composer.templateFlow.saveSeed}
            onClose={composer.templateFlow.closeSaveDrawer}
            onSaved={composer.templateFlow.onTemplateSaved}
            showBack={variant === 'inline' || variant === 'modal'}
          />
        </>
      )}
    </>
  );

  if (variant === 'inline') {
    // Fills the host panel (the contact drawer's right column already owns the
    // surface + scroll). No absolute positioning / slide / shadow, so it lays out
    // in-flow instead of the drawer variant's off-canvas 620px overlay.
    // Deliberately NOT a positioning context — see the positioning invariant on
    // SaveAsTemplatePanel.
    return (
      <Box display="flex" flexDirection="column" h="100%" minH={0}>
        {content}
      </Box>
    );
  }

  if (variant === 'modal') {
    // Deliberately NOT a positioning context — see the positioning invariant on
    // SaveAsTemplatePanel.
    // The composer gets a set height, not only a cap: the canvas scrolls its body
    // inside the height it is handed, and under a bare max-height it grows with a
    // long template until the bottom of the dialog is cut off. Same approach as
    // the bulk composers' dialog, which sizes its content outright.
    return (
      <Box
        display="flex"
        flexDirection="column"
        overflow="visible"
        h={composer.isConnected ? 'calc(100vh - 160px)' : undefined}
        maxH="720px"
      >
        {content}
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      style={{
        position: 'absolute',
        right: '100%',
        top: 0,
        bottom: 0,
        width: '620px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        position="absolute"
        inset={0}
        bg="white"
        bgGradient="to-b"
        gradientFrom="seaSalt/50"
        gradientTo="white"
        borderLeft="1px solid"
        borderColor="border"
        boxShadow="-12px 0 40px -8px rgba(0,0,0,0.12)"
        display="flex"
        flexDirection="column"
      >
        {content}
      </Box>
    </motion.div>
  );
};

export default SingleRecipientEmailPanel;
